import { FacetConfiguration } from 'components/commercetools-ui/organisms/product/product-list-algolia/types'

export const DEFAULT_PLP_FACETS_CONFIGURATION_TITLE = 'PLP'

/**
 * Contentstack commercetools category custom fields expose keys under `data`.
 * Generated types sometimes describe a `value` select shape — support both.
 */
export type MainCategoryField =
  | { value?: { key?: string; value?: string }[]; data?: { key?: string }[] }
  | undefined

export type AlgoliaFacetsConfigurationEntryMeta = {
  title: string
  /** Contentstack `main_category` — contains DE / AT / CZ root category keys for the same Hauptkategorie. */
  main_category?: MainCategoryField
}

export type AlgoliaFacetsConfigurationIndex = {
  byTitle: Record<string, Record<string, FacetConfiguration>>
  /** Indexed by every commercetools category key from `main_category` (DE / AT / CZ roots share one entry). */
  byCategoryKey: Record<string, Record<string, FacetConfiguration>>
}

export function extractMainCategoryKeys(mainCategory: MainCategoryField): string[] {
  if (!mainCategory) return []

  const fromValue = Array.isArray(mainCategory.value)
    ? mainCategory.value.map((item) => item?.key)
    : []
  const fromData = Array.isArray(mainCategory.data)
    ? mainCategory.data.map((item) => item?.key)
    : []

  return [...fromValue, ...fromData].filter((key): key is string => typeof key === 'string' && key.length > 0)
}

/**
 * Builds lookup indexes from mapped facets configurations.
 * Entries with `main_category` are registered under each listed category key (all markets).
 * Title `PLP` remains the default fallback when no main-category override matches.
 * Duplicate category keys keep the first entry; duplicate titles keep the last
 * (Contentstack fetch is `ascending('uid')`, so last is the newest entry).
 */
export function buildAlgoliaFacetsConfigurationIndex(
  entries: (AlgoliaFacetsConfigurationEntryMeta & { facetsConfiguration: Record<string, FacetConfiguration> })[],
): AlgoliaFacetsConfigurationIndex {
  const byTitle: AlgoliaFacetsConfigurationIndex['byTitle'] = {}
  const byCategoryKey: AlgoliaFacetsConfigurationIndex['byCategoryKey'] = {}

  for (const entry of entries) {
    if (entry.title) {
      byTitle[entry.title] = entry.facetsConfiguration
    }

    for (const categoryKey of extractMainCategoryKeys(entry.main_category)) {
      if (!byCategoryKey[categoryKey]) {
        byCategoryKey[categoryKey] = entry.facetsConfiguration
      }
    }
  }

  return { byTitle, byCategoryKey }
}
